import get from 'lodash/get'
import * as api from '.'
import { NOTIFICATIONS_SET_ALL, NOTIFICATIONS_SET_MENU } from './actions'

/**
 * How many rows one bell-dropdown tab previews.
 *
 * Matches the ten the unfiltered bell list already fetches, so a tab shows the same depth the
 * dropdown showed before it had tabs. "View All" is right beneath it for anything deeper.
 */
export const MENU_PREVIEW_PER_PAGE = 10

/**
 * Helpers for `watchers.js`, which cannot hold them itself: `configureStore` spreads EVERY
 * export of that module into `witness.createWatchers`, which calls each one with the effect
 * factories and throws unless it gets an `Effect` back. So anything exported from `watchers.js`
 * must be a watcher. (Same reason `user-profiles` has a `watchers-helpers.js`.)
 *
 * @module services/notifications/watchers-helpers
 */

/**
 * Fetch one page of one tab for the `/notifications` page (CO-768).
 *
 * `category` is closed over rather than read off the action, so each tab can get its own
 * `takeLatest` effect — see the two watchers in `watchers.js` for why that matters.
 *
 * `fetch` is injectable only so the dispatch shape (category passed through, append above page
 * one, `failed` on a swallowed error) can be asserted without a network.
 */
export function createGetNotificationsWorker (category, fetch = api.getNotifications) {
  return async ({ state, action }) => {
    const { auth, user } = state
    const language = user.getIn(['data', 'language'], ['en'])
    const after = get(action, ['payload', 'after']) || undefined
    const result = await fetch({
      auth, language, category, after,
    })
    // `api.getNotifications` swallows the error and answers `{ success: false }`. Flag it rather
    // than writing an empty page, so a blip on "Load more" does not truncate the list the member
    // is reading.
    if (get(result, 'success') === false) {
      return {
        type: NOTIFICATIONS_SET_ALL,
        payload: { category, after, failed: true },
      }
    }
    return {
      type: NOTIFICATIONS_SET_ALL,
      payload: {
        category,
        // ⚠️ Cursor paging. The first page has no `after` and REPLACES; every page fetched with
        // one APPENDS. Never derive a page index from a cursor — notifications arrive at the top
        // of this list constantly, which is exactly when an offset repeats or skips a row.
        append: !!after,
        rows: get(result, 'rows', []),
        total: get(result, 'total'),
        hasNextPage: get(result, 'hasNextPage', false),
        endCursor: get(result, 'endCursor', null),
      },
    }
  }
}

/**
 * Fetch one bell-dropdown TAB's preview rows (CO-768).
 *
 * Same shape as `createGetNotificationsWorker` above and deliberately not merged with it: that
 * one carries page state (`p`, `append`, `total`) because the page paginates, and this one has
 * none because a preview does not. Folding them together would mean a worker that writes page
 * fields the dropdown never reads, into a slice the page never reads.
 *
 * `category` is closed over rather than read off the action so each tab gets its own
 * `takeLatest` — see the watchers for why a shared one strands the other tab's spinner.
 */
export function createGetMenuNotificationsWorker (category, fetch = api.getNotifications) {
  return async ({ state }) => {
    const { auth, user } = state
    const language = user.getIn(['data', 'language'], ['en'])
    const result = await fetch({
      auth, language, category, p: 1, pp: MENU_PREVIEW_PER_PAGE,
    })
    // `api.getNotifications` swallows the error and answers `{ success: false }`. Flagged rather
    // than written as an empty page, so a blip does not tell a member their tab is empty.
    if (get(result, 'success') === false) {
      return {
        type: NOTIFICATIONS_SET_MENU,
        payload: { category, failed: true },
      }
    }
    return {
      type: NOTIFICATIONS_SET_MENU,
      payload: { category, rows: get(result, 'rows', []) },
    }
  }
}
